import { useInView } from '@/hooks/useInView'
import { CRAFT_PILLARS, FINISH_PHILOSOPHY } from '@/data/craft'

const reveal = (inView: boolean) =>
  `transition-all duration-1000 ease-out-expo ${inView ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-8 blur-sm'}`

const STAGGER = [
  '[transition-delay:0ms]',
  '[transition-delay:90ms]',
  '[transition-delay:180ms]',
  '[transition-delay:270ms]',
  '[transition-delay:360ms]',
  '[transition-delay:450ms]',
]

/**
 * The small-batch engineering + boutique finish story (docs/BRAND-CONTEXT.md).
 * Shared by the landing and about pages.
 */
export function CraftSection() {
  const { ref: headerRef, inView: headerIn } = useInView<HTMLDivElement>(0.15, 'craft-header')
  const { ref: gridRef, inView: gridIn } = useInView<HTMLDivElement>(0.1, 'craft-grid')
  const { ref: finishRef, inView: finishIn } = useInView<HTMLDivElement>(0.15, 'craft-finish')

  return (
    <section id="craft" className="relative py-32 bg-carbon-950 texture-noise overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div ref={headerRef} className="max-w-3xl">
          <p className={`text-gold-500 font-mono text-xs tracking-[0.3em] uppercase mb-6 ${reveal(headerIn)} [transition-delay:0s]`}>
            Small-Batch Engineering
          </p>
          <h2 className={`font-display font-bold text-4xl md:text-5xl text-carbon-50 uppercase leading-tight mb-6 ${reveal(headerIn)} [transition-delay:0.1s]`}>
            Made to order.
            <br />
            <span className="text-titanium-400">Made properly.</span>
          </h2>
          <p className={`text-titanium-400 text-lg leading-relaxed ${reveal(headerIn)} [transition-delay:0.25s]`}>
            A Burton is not custom because you choose the colour. It is custom because the
            frame, geometry, construction method, materials, and finish are all shaped
            around one rider — using manufacturing built for one-of-one machines, not
            factory moulds.
          </p>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-16 ${reveal(headerIn)} [transition-delay:0.35s]`}>
          <figure className="relative overflow-hidden bg-carbon-900 border border-carbon-700">
            <video
              src="/media/raptor/raptor-suspension-animation.mp4"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full aspect-video object-cover"
            />
            <figcaption className="absolute bottom-0 inset-x-0 flex items-center justify-between px-5 py-3 bg-gradient-to-t from-carbon-950/90 to-transparent">
              <span className="font-mono text-2xs tracking-[0.25em] uppercase text-titanium-400">
                KBL suspension — motion study
              </span>
              <span className="font-mono text-2xs text-gold-500">Fig. 01</span>
            </figcaption>
          </figure>
          <figure className="relative overflow-hidden bg-carbon-900 border border-carbon-700">
            <video
              src="/media/raptor/raptor-motion-study.mp4"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full aspect-video object-cover"
            />
            <figcaption className="absolute bottom-0 inset-x-0 flex items-center justify-between px-5 py-3 bg-gradient-to-t from-carbon-950/90 to-transparent">
              <span className="font-mono text-2xs tracking-[0.25em] uppercase text-titanium-400">
                Raptor I — development render
              </span>
              <span className="font-mono text-2xs text-gold-500">Fig. 02</span>
            </figcaption>
          </figure>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 gap-px bg-carbon-700 border border-carbon-700 mt-4">
          {CRAFT_PILLARS.map((pillar, i) => (
            <div
              key={pillar.id}
              className={`group bg-carbon-900 p-8 md:p-10 flex flex-col gap-4 hover:bg-carbon-800 transition-[background-color,opacity,transform,filter] duration-700 ease-out-expo ${STAGGER[i]} ${
                gridIn ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-8 blur-sm'
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs text-gold-500/70 tracking-widest">{pillar.index}</span>
                <span className="flex-1 h-px bg-carbon-700 group-hover:bg-gold-500/30 transition-colors duration-500" />
                <span className="font-mono text-2xs tracking-[0.3em] uppercase text-titanium-600">{pillar.label}</span>
              </div>
              <h3 className="font-display font-bold text-xl md:text-2xl text-carbon-50 uppercase">{pillar.title}</h3>
              <p className="text-titanium-400 text-sm leading-relaxed">{pillar.body}</p>
            </div>
          ))}
        </div>

        <div
          ref={finishRef}
          className={`relative mt-16 border border-gold-500/25 bg-gradient-to-br from-carbon-900 to-carbon-950 p-10 md:p-14 overflow-hidden ${reveal(finishIn)} [transition-delay:0.1s]`}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute -right-6 -bottom-10 font-display font-extrabold italic uppercase tracking-tight [font-stretch:125%] leading-none text-[8rem] md:text-[12rem] text-gold-500/[0.05] select-none"
          >
            1/1
          </span>
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="flex flex-col gap-5">
              <p className="text-gold-500 font-mono text-xs tracking-[0.3em] uppercase">{FINISH_PHILOSOPHY.eyebrow}</p>
              <h3 className="font-display font-bold text-3xl md:text-4xl text-carbon-50 uppercase leading-tight">
                {FINISH_PHILOSOPHY.heading}
              </h3>
              <p className="text-titanium-300 text-base md:text-lg leading-relaxed">{FINISH_PHILOSOPHY.body}</p>
              <p className="font-mono text-xs leading-relaxed tracking-wide text-titanium-500 uppercase">
                {FINISH_PHILOSOPHY.note}
              </p>
            </div>
            <figure className="relative overflow-hidden border border-carbon-700">
              <img
                src="/images/raptor/raptor-frame-render.png"
                alt="Raptor I chassis in the raw house finish — filament-wound carbon tubes and additive-manufactured lugs"
                className="w-full aspect-[16/10] object-cover"
              />
              <figcaption className="absolute bottom-0 inset-x-0 px-5 py-3 bg-gradient-to-t from-carbon-950/90 to-transparent">
                <span className="font-mono text-2xs tracking-[0.25em] uppercase text-titanium-400">
                  Raptor I — the raw house finish
                </span>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  )
}
